(() => {
  'use strict';

  let sharedObserver = null;

  const getObserver = () => {
    if (sharedObserver || !('IntersectionObserver' in window)) return sharedObserver;
    sharedObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        sharedObserver.unobserve(entry.target);
        entry.target.load();
      });
    }, { rootMargin: '500px 0px' });
    return sharedObserver;
  };

  class JustStartProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded === 'true' || this.dataset.loaded === 'loading') return;
      const observer = getObserver();
      if (!observer) {
        this.load();
        return;
      }
      observer.observe(this);
    }

    disconnectedCallback() {
      if (sharedObserver) sharedObserver.unobserve(this);
      if (this.controller) this.controller.abort();
    }

    async load() {
      if (this.dataset.loaded === 'true' || this.dataset.loaded === 'loading') return;
      this.dataset.loaded = 'loading';
      this.controller = new AbortController();
      try {
        const response = await fetch(this.dataset.url, {
          credentials: 'same-origin',
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error(`Recommendations ${response.status}`);
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        /* A product page now asks for two different intents — related and
           complementary — from the same section. Both come back inside the
           same re-rendered section, so the element has to find its own block
           by key rather than taking whichever one appears first. */
        const key = this.dataset.recKey;
        const source = key
          ? [...documentFragment.querySelectorAll('jst-product-recommendations')]
            .find((candidate) => candidate.dataset.recKey === key)
          : documentFragment.querySelector('jst-product-recommendations');
        if (!source || !source.innerHTML.trim()) {
          this.dataset.loaded = 'true';
          this.hidden = true;
          return;
        }
        this.innerHTML = source.innerHTML;
        this.dataset.loaded = 'true';
        dedupeAcrossRails();
        document.dispatchEvent(new CustomEvent('jst:recommendations-loaded', { detail: { element: this } }));
      } catch (error) {
        if (error.name === 'AbortError') {
          delete this.dataset.loaded;
          return;
        }
        /* A failed or slow endpoint removes the block and says nothing. No
           spinner is left behind, no error reaches the console, and the rest
           of the page — including buying — is untouched. */
        this.hidden = true;
        this.dataset.loaded = 'true';
      }
    }
  }

  /* Each intent is a separate HTTP request, so the server cannot know what the
     other rail already rendered. Reconciling here is the only place both lists
     exist at once. Priority follows document order: the first rail on the page
     keeps the product, later rails drop it. A rail emptied by this loses its
     heading too, rather than standing as an empty titled block. */
  const dedupeAcrossRails = () => {
    const seen = new Set();
    document.querySelectorAll('jst-product-recommendations [data-jst-rail]').forEach((rail) => {
      rail.querySelectorAll('.jst-card[data-product-handle]').forEach((card) => {
        const handle = card.dataset.productHandle;
        if (!seen.has(handle)) {
          seen.add(handle);
          return;
        }
        const item = card.closest('.jst-rail__item') || card;
        item.remove();
      });
      if (!rail.querySelector('.jst-card')) {
        const host = rail.closest('jst-product-recommendations');
        if (host) host.hidden = true;
      }
    });
  };

  if (!customElements.get('jst-product-recommendations')) {
    customElements.define('jst-product-recommendations', JustStartProductRecommendations);
  }
})();
